import * as angular from 'angular';

import { ComponentDefinition, ObjectUser } from '../../../models';
import { groupIdentifierSeparator } from '../../../common/constants';

interface IExtendedObjectUser {
    destinationState?: string;
    displayName?: string;
    icon?: string;
    navigationParameters?: any;
    stateParams?: any;
    type?: string;
    usage: ObjectUser;
}

interface INavigationParameters {
    groupGuid: string;
    identifierName: string;
    state: string;
}

const identifierTypesDictionary = {
    'Validated identifier': {
        icon: 'fa fa-fw fa-check-square-o',
        type: 'Validated, target or calculated identifier in group'
    },
    'Identifier': {
        icon: 'fa fa-fw fa-tag',
        type: 'Identifier in group'
    },
    'Measured identifier': {
        icon: 'fa fa-fw fa-tag',
        type: 'Identifier in group'
    },
    'Target identifier': {
        icon: 'fa fa-fw fa-dot-circle-o',
        type: 'Validated, target or calculated identifier in group'
    },
    'Calculated identifier': {
        icon: 'fa fa-fw fa-calculator',
        type: 'Validated, target or calculated identifier in group'
    }
};

class ModalCommonUsageController implements angular.IComponentController {
    alert: string;
    extendedUsages: IExtendedObjectUser[];
    item: any;
    message: string;
    modalId: string;
    usages: ObjectUser[];

    static $inject = ['$state', '$translate'];

    constructor(private _$state: angular.ui.IStateService, private _$translate: angular.translate.ITranslateService) {
    }

    $onInit() {
        // Ensure retro-compatibility
        if (!this.usages && this.item && this.item.users) {
            this.usages = this.item.users;
        }

        if (this.usages) {
            this.extendedUsages = this.fillExtendedUsages(this.usages);
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['usages'] && changesObj['usages'].currentValue) {
            const usages = changesObj['usages'].currentValue;
            if (usages) {
                this.extendedUsages = this.fillExtendedUsages(usages);
            }
        }
    }

    navigate(parameters: INavigationParameters) {
        if (this._$state.is(parameters.state, { groupGuid: parameters.groupGuid, identifierName: parameters.identifierName })) {
            this.close();
        } else {
            this._$state.go(parameters.state, { groupGuid: parameters.groupGuid, identifierName: parameters.identifierName });
        }
    }

    close(): void {
        angular.element(this.modalId).modal('hide');
    }

    yesDisabled(): boolean {
        return true;
    }

    private fillExtendedUsages(usages: ObjectUser[]): IExtendedObjectUser[] {
        return usages.map(usage => {
            let extendedUsage: IExtendedObjectUser = {
                usage: usage
            };
            extendedUsage = this.fillExtendedProperties(extendedUsage);
            return extendedUsage;
        });
    }

    private fillExtendedProperties(extendedUsage: IExtendedObjectUser): IExtendedObjectUser {
        if (extendedUsage?.usage) {
            const { type } = extendedUsage.usage;
            switch (type) {
                case 'Dashboard':
                    extendedUsage.displayName = `${this._$translate.instant(extendedUsage.usage.type)} ${extendedUsage.usage.name}`;
                    this.completeIfOwner(extendedUsage);
                    extendedUsage.destinationState = 'dashboard';
                    extendedUsage.stateParams = { dashboardName: extendedUsage.usage.guid };
                    extendedUsage.icon = 'fa fa-fw fa-th-large';
                    extendedUsage.type = 'Dashboard';
                    break;
                case 'Trend':
                    extendedUsage.displayName = `${this._$translate.instant(extendedUsage.usage.type)} ${extendedUsage.usage.name}`;
                    this.completeIfOwner(extendedUsage);
                    extendedUsage.destinationState = 'trend';
                    extendedUsage.stateParams = { trendGuid: extendedUsage.usage.guid };
                    extendedUsage.icon = 'fa fa-fw fa-line-chart';
                    extendedUsage.type = 'Trend';
                    break;
                case 'HistorianStorageConfiguration':
                    extendedUsage.displayName = `${this._$translate.instant(extendedUsage.usage.type)} ${extendedUsage.usage.name}`;
                    this.completeIfOwner(extendedUsage);
                    extendedUsage.destinationState = 'moreTools.exportConfiguration';
                    extendedUsage.stateParams = { historianStorageGuid: extendedUsage.usage.guid };
                    extendedUsage.icon = 'fa fa-fw fa-upload';
                    extendedUsage.type = 'Historian Storage';
                    break;
                case 'Group':
                    this.setGroupProperties(extendedUsage);
                    break;
                case 'ValidationPage':
                    extendedUsage.displayName = `${this._$translate.instant('Validation page')} ${extendedUsage.usage.name}`;
                    this.completeIfOwner(extendedUsage);
                    extendedUsage.destinationState = 'group.validation';
                    extendedUsage.stateParams = { groupGuid: extendedUsage.usage.guid };
                    extendedUsage.icon = 'fa fa-fw fa-check-square-o';
                    extendedUsage.type = 'Validation page';
                    break;
                case 'ValidationPageReference':
                    extendedUsage.displayName = `${this._$translate.instant('Validation page reference for type')} ${extendedUsage.usage.name}`;
                    this.completeIfOwner(extendedUsage);
                    extendedUsage.destinationState = 'models.structure';
                    extendedUsage.stateParams = { groupGuid: extendedUsage.usage.guid };
                    extendedUsage.icon = 'fa fa-fw fa-check-square-o';
                    extendedUsage.type = 'Validation page reference';
                    break;
                case 'Scheduled e-mail':
                    extendedUsage.displayName = `${this._$translate.instant('Scheduled e-mail')} ${extendedUsage.usage.name}`;
                    extendedUsage.destinationState = 'editEmail';
                    extendedUsage.stateParams = { guid: extendedUsage.usage.guid };
                    extendedUsage.icon = 'fa fa-fw fa-envelope-o';
                    extendedUsage.type = 'Historian Storage';
                    break;
                case 'TagDefinition':
                    extendedUsage.displayName = `${this._$translate.instant('Tag')} ${extendedUsage.usage.ownerName}`;
                    extendedUsage.destinationState = 'tag.general';
                    extendedUsage.stateParams = { tagDefinitionGuid: extendedUsage.usage.ownerGuid };
                    extendedUsage.icon = 'fa fa-fw fa-tag';
                    break;
                case 'Identifier':
                    extendedUsage.displayName = `${this._$translate.instant('Identifier')} ${extendedUsage.usage.name}${groupIdentifierSeparator}${extendedUsage.usage.ownerName}`;
                    extendedUsage.destinationState = 'group.identifiers';
                    extendedUsage.stateParams = { groupGuid: extendedUsage.usage.guid };
                    extendedUsage.icon = 'fa fa-fw fa-chain';
                    break;                  
                case 'User group':
                    this.setUserGroupProperties(extendedUsage);
                    break;
            }
        }
        return extendedUsage;
    }

    private setUserGroupProperties(extendedUsage: IExtendedObjectUser){
        const { ownerType } = extendedUsage.usage;
        if (ownerType === 'Email template') {
            extendedUsage.displayName = `${this._$translate.instant('Email template')} ${extendedUsage.usage.ownerName}`;                    
            extendedUsage.destinationState = 'workflowsOverview';
            extendedUsage.icon = 'fa fa-fw fa-envelope-o';
            extendedUsage.type = 'Email template';
        }
    }

    private setGroupProperties(extendedUsage: IExtendedObjectUser) {
        const { ownerType } = extendedUsage.usage;
        if (ownerType === 'Trend') {
            extendedUsage.displayName = `${this._$translate.instant('Group trends')} ${this._$translate.instant(extendedUsage.usage.type)} ${extendedUsage.usage.name}`;
            this.completeIfOwner(extendedUsage);
            extendedUsage.destinationState = 'group.trend';
            extendedUsage.stateParams = { groupGuid: extendedUsage.usage.guid };
            extendedUsage.icon = 'fa fa-fw fa-line-chart';
            extendedUsage.type = 'Group trend';
        } else if (ownerType === 'Script') {
            extendedUsage.displayName = `${this._$translate.instant('Script of _selectedGroupName_', { selectedGroupName: extendedUsage.usage.name })}`;
            extendedUsage.destinationState = 'group.script';
            extendedUsage.stateParams = { groupGuid: extendedUsage.usage.guid };
            extendedUsage.icon = 'fa fa-fw fa-code';
            extendedUsage.type = 'Group script';
        } else if (ownerType === 'Alarms') {
            extendedUsage.displayName = `${this._$translate.instant('Alarms of _selectedGroupName_', { selectedGroupName: extendedUsage.usage.name })}`;
            extendedUsage.destinationState = 'group.alarms';
            extendedUsage.stateParams = { groupGuid: extendedUsage.usage.guid };
            extendedUsage.icon = 'fa fa-fw fa-bell';
            extendedUsage.type = 'Group alarm';
        } else if (ownerType === 'Rules') {
            extendedUsage.displayName = `${this._$translate.instant('Rules of _selectedGroupName_', { selectedGroupName: extendedUsage.usage.name })}`;
            extendedUsage.destinationState = 'group.rules';
            extendedUsage.stateParams = { groupGuid: extendedUsage.usage.guid };
            extendedUsage.icon = 'fa fa-fw fa-signal';
            extendedUsage.type = 'Group rule';
        } else if ((ownerType === 'Linked' || ownerType === 'Aggregated')) {
            if (ownerType === 'Linked') {
                extendedUsage.displayName = `${this._$translate.instant('Group _groupUniqueName_ linked identifier _identifierName_', { groupUniqueName: extendedUsage.usage.name, identifierName: extendedUsage.usage.ownerName })}`;
                extendedUsage.icon = 'fa fa-fw fa-chain';
                extendedUsage.type = 'Linked identifier in group';
            }
            else if (ownerType === 'Aggregated') {
                extendedUsage.displayName = `${this._$translate.instant('Group _groupUniqueName_ aggregated identifier _identifierName_', { groupUniqueName: extendedUsage.usage.name, identifierName: extendedUsage.usage.ownerName })}`;
                extendedUsage.icon = 'fa fa-fw fa-calculator';
                extendedUsage.type = 'Aggregated identifier in group';
            }
            extendedUsage.destinationState = 'group.identifiers';
            extendedUsage.stateParams = { groupGuid: extendedUsage.usage.guid };
        } else if (identifierTypesDictionary[ownerType]) {
            Object.assign(extendedUsage, identifierTypesDictionary[ownerType]);
            extendedUsage.displayName = `${this._$translate.instant(ownerType)} ${extendedUsage.usage.ownerName} (${this._$translate.instant('Used in')} ${extendedUsage.usage.name})`;
            extendedUsage.navigationParameters = { state: 'group.identifiers', groupGuid: extendedUsage.usage.guid, identifierName: extendedUsage.usage.ownerName };
        }
    }

    private completeIfOwner(extended: IExtendedObjectUser) {
        if (extended.usage.ownerName) {
            extended.displayName = `${extended.displayName} (${extended.usage.ownerName})`;
        }
    }
}

export const wtModalCommonUsage: ComponentDefinition = {
    componentId: 'wtModalCommonUsage',
    controller: ModalCommonUsageController,
    template: require('./modalCommonUsage.component.html'),
    controllerAs: 'vm',
    bindings: {
        alert: '@?',
        item: '<',
        usages: '<',
        message: '@',
        modalId: '@',
    }
};